import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from '../locales/en/translation.json';
import pt from '../locales/pt/translation.json';

const resources = {
  en: { translation: en },
  pt: { translation: pt },
};

const SECTION_OVERRIDES = {
  marketplace_page: {
    title: 'AI Agent Marketplace',
    subtitle: 'Pre-built AI agents for business',
    self_service: 'Self-service',
    browse_agents: 'Browse agents',
    compare_pricing: 'Compare pricing',
    hero_note: 'Choose a vertical, launch in minutes, and scale when you are ready.',
    breadcrumb_marketplace: 'Marketplace',
    how_it_works: 'How it works',
    how_it_works_subtitle: 'Pick a vertical, connect your systems, and go live.',
    agents_by_industry: 'Agents by industry',
    agents_subtitle: 'Choose the pre-built agent that fits your workflow.',
    view_agent: 'View agent',
    skills_included: 'Skills included',
    skills_intro: 'Each agent ships with a curated skill pack.',
    pricing_note: 'Need something custom? Compare pricing for cloud and enterprise plans.',
    see_full_pricing: 'See full pricing',
    faq_title: 'Frequently asked questions',
    faq_subtitle: 'Everything you need to know before launching.',
    integrations_title: 'Integrations',
    integrations_subtitle: 'Connect the tools you already use.',
    view_all_integrations: 'View all integrations',
    cta_title: 'Ready to launch your agent?',
    cta_subtitle: 'Start self-service or talk to the team.',
    talk_to_team: 'Talk to the team',
    try_free: 'Try free',
    skills_for: 'Skills for',
    your_agent: 'your agent',
    skills_desc: 'Add pre-built skills to extend what your agent can do.',
    view_all_skills: 'View all skills',
  },
  cs_page: {
    marketplace_badge: 'Marketplace',
    title: 'AI Customer Support Agent',
    subtitle: 'Automate support across WhatsApp, Telegram, email, and Discord with fast human escalation when needed.',
    deploy_cta: 'Deploy now',
    activate_cta: 'Activate now',
    back_to_marketplace: 'Back to marketplace',
    breadcrumb_cs: 'Customer Support',
    features_title: 'Why teams choose it',
    features_subtitle: 'Resolve tickets faster, keep context, and scale support without hiring more headcount.',
    how_it_works: 'How it works',
    how_it_works_subtitle: 'Connect channels, upload knowledge, set rules, and go live.',
    channels_title: 'Supported channels',
    channels_subtitle: 'Meet customers where they are already talking.',
    pricing_title: 'Pricing',
    pricing_subtitle: 'Choose self-service or a managed setup.',
    faq_title: 'Frequently asked questions',
    faq_subtitle: 'Answers before you launch.',
    testimonials_title: 'What customers say',
    testimonials_subtitle: 'Real outcomes from support teams.',
    final_cta_title: 'Launch customer support automation today',
    final_cta_subtitle: 'Stop answering the same questions by hand.',
    pricing_note: 'See full pricing for cloud and enterprise options.',
    try_free: 'Try free',
    coming_soon: 'Coming soon',
    other_agents: 'Other agents',
    price: 'Price',
    included_label: 'Included',
    early_adopter: 'Early adopter',
    resolution_badge: 'Resolution',
    case_study_title: 'Case study',
    case_study_desc: 'How teams use support automation to cut response time.',
    demo_title: 'Live demo',
    demo_subtitle: 'Preview the customer experience in action.',
    agent_name: 'Agent name',
    agent_status: 'Status',
    agent_consultancy: 'Consultancy',
    agent_ecommerce: 'E-commerce',
    agent_marketing: 'Marketing',
    agent_realestate: 'Real estate',
    channel_whatsapp: 'WhatsApp',
    channel_telegram: 'Telegram',
    channel_email: 'Email',
    channel_discord: 'Discord',
    channel_slack: 'Slack',
    channel_zendesk: 'Zendesk',
    channel_intercom: 'Intercom',
    pricing_cta: 'Compare plans',
    activate_button: 'Activate',
  },
  register: {
    subtitle: 'Create an account to purchase, download, and deploy your AI digital worker.',
    create_account: 'Create account',
    continue_google: 'Continue with Google',
    or_email: 'or with email',
    name: 'Name',
    email: 'Email',
    password: 'Password',
    password_placeholder: 'At least 6 characters',
    hide_password: 'Hide password',
    show_password: 'Show password',
    password_hint: 'Minimum 6 characters',
    creating: 'Creating...',
    create_account_button: 'Create account',
    has_account: 'Have an account?',
    sign_in: 'Sign in',
    try_again: 'Please try again.',
    jane_doe: 'Jane Doe',
  },
  product_detail: {
    loading_title: 'Loading product…',
    loading_desc: 'Loading product details.',
    loading_text: 'Loading product…',
    not_found_title: 'Product not found',
    back_to_catalog: 'Back to catalog',
    catalog: 'Catalog',
    checkout_error: 'Checkout failed. Please try again.',
    buy_now: 'Buy now',
    see_inside: 'See inside',
    secure_checkout: 'Secure checkout',
    instant_access: 'Instant access',
    thirty_day_support: '30-day support',
    pricing_label: 'Pricing',
    setup_label: 'Setup',
    starts_at_0: 'Starts at $0',
    setup_assist: 'Setup Assist',
    setup_assist_desc: 'We handle onboarding and configuration for you.',
    learn_more: 'Learn more',
    recurring_subscription: 'Recurring subscription',
    promo_setup_note: 'Setup is discounted for a limited time.',
    setup_from: 'Setup from',
    setup_includes: 'Setup includes',
    setup_item_1: 'Account configuration',
    setup_item_2: 'Channel connections',
    setup_item_3: 'Knowledge base import',
    setup_item_4: 'Escalation rules',
    setup_item_5: 'Launch support',
    signin_required: 'Sign in required',
    preview_label: 'Preview',
    whats_included_label: "What's included",
    whats_included: "What's included",
    active_agents: 'Active agents',
    agents_label: 'Agents',
    ai_department: 'AI department',
    all_systems_operational: 'All systems operational',
    benefits_label: 'Benefits',
    command_center: 'Command center',
    compare_plans: 'Compare plans',
    compute: 'Compute',
    control_center: 'Control center',
    dashboard_desc: 'Dashboard overview',
    dashboard_label: 'Dashboard',
    enterprise_command_center: 'Enterprise command center',
    enterprise_online: 'Enterprise online',
    enterprise_ops_label: 'Enterprise ops',
    escalations: 'Escalations',
    events_label: 'Events',
    executor: 'Executor',
    executors: 'Executors',
    full_content_unlocks: 'Full content unlocks',
    get_now_1: 'Get now',
    get_now_2: 'Get now',
    inside_course: 'Inside course',
    lifetime_access: 'Lifetime access',
    live_day_one: 'Live on day one',
    multi_agent_desc: 'Multi-agent setup description',
    one_command_center: 'One command center',
    plan_enterprise: 'Enterprise plan',
    plan_growth: 'Growth plan',
    planner: 'Planner',
    ready_label: 'Ready',
    reviewer: 'Reviewer',
    reviews: 'Reviews',
    setup_yourself: 'Set up yourself',
    storage: 'Storage',
    tasks_today: 'Tasks today',
    timeline: 'Timeline',
    upgrade_enterprise: 'Upgrade to Enterprise',
    view_dashboard_preview: 'View dashboard preview',
    why_choose: 'Why choose this',
    has_account: 'Have an account?',
  },
  nav: {
    pricing: 'Pricing',
  },
  common: {
    saving: 'Saving...',
  },
};

function humanizeKey(key) {
  const last = key.split('.').pop() || key;
  const replacements = {
    api: 'API',
    cta: 'CTA',
    cs: 'CS',
    faq: 'FAQ',
    sms: 'SMS',
    seo: 'SEO',
    slack: 'Slack',
    smtp: 'SMTP',
    imap: 'IMAP',
    ui: 'UI',
    url: 'URL',
    whatsapp: 'WhatsApp',
    x: 'X',
  };
  return last
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .map((word) => replacements[word.toLowerCase()] || word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .trim();
}

function parseMissingKeyHandler(key) {
  const [section, ...rest] = key.split('.');
  const sectionOverrides = SECTION_OVERRIDES[section];
  if (sectionOverrides) {
    const exact = rest.join('.');
    if (sectionOverrides[exact]) return sectionOverrides[exact];
    if (sectionOverrides[rest[0]]) return sectionOverrides[rest[0]];
  }
  return humanizeKey(key);
}

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: 'en',
    fallbackLng: 'en',
    parseMissingKeyHandler,
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;
